/**
 * Z-index constants for consistent stacking context management
 * Values are designed to work with Tailwind's z-index utilities
 *
 * HIERARCHY (lowest to highest):
 * 1. GAME_PLAYER_LOADING_OVERLAY (z-10) - Octopus loader over player
 * 2. GAME_THEATER_HEADER (z-20) - Game header controls (above loader overlap)
 * 3. GAME_NPS_OVERLAY (z-30) - Sticky NPS after game close (site chrome)
 * 4. DIALOG (z-50) - Basic dialog/modal level
 * 5. FULLSCREEN_CONTAINER (z-[9999]) - Fullscreen game/video
 * 6. MOBILE_MENU_OVERLAY / GAME_SHARE_SHEET (z-[10000]) - Overlay behind mobile menu;
 *    game share sheet (portaled to body, must sit above theater/fullscreen)
 * 7. MOBILE_MENU (z-[10001]) - Mobile navigation (MUST always be accessible)
 *
 * IMPORTANT: Mobile menu must ALWAYS be on top of all content including
 * fullscreen containers to ensure navigation remains accessible.
 */

/** Numeric z-index values for programmatic comparison and testing */
export const Z_INDEX_VALUES = {
  GAME_PLAYER_LOADING_OVERLAY: 10,
  GAME_THEATER_HEADER: 20,
  GAME_NPS_OVERLAY: 30,
  DIALOG: 50,
  FULLSCREEN_CONTAINER: 9999,
  MOBILE_MENU_OVERLAY: 10000,
  GAME_SHARE_SHEET: 10000,
  MOBILE_MENU: 10001,
} as const;

/**
 * Tailwind-compatible z-index classes
 * Use these in className strings: `${Z_INDEX_CLASSES.FULLSCREEN_CONTAINER}`
 * Note: These constants already include the "z-[...]" prefix, so use them directly.
 */
export const Z_INDEX_CLASSES = {
  GAME_PLAYER_LOADING_OVERLAY: "z-10",
  GAME_THEATER_HEADER: "z-20",
  GAME_NPS_OVERLAY: "z-30",
  DIALOG: "z-50",
  FULLSCREEN_CONTAINER: "z-[9999]",
  MOBILE_MENU_OVERLAY: "z-[10000]",
  GAME_SHARE_SHEET: "z-[10000]",
  MOBILE_MENU: "z-[10001]",
} as const;
